import React, { useEffect, useState, useCallback } from "react";
import "./ClientDetail.css";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper,
} from "@mui/material";
import {
  UilArrowLeft, UilPlus, UilEdit, UilTrash,
  UilMapPin, UilPhone,
} from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";

const TABS = ["Contacts", "Conditions commerciales", "Notes CRM"];
const NOTE_TYPES = ["call", "email", "meeting", "other"];
const PAYMENT_MODES = ["bank-transfer", "check", "cash", "credit"];
const TAX_RULES = ["HT", "TTC", "exempt"];

// ── Contacts Tab ─────────────────────────────────────────────────────────────
const ContactsTab = ({ clientId }) => {
  const [contacts, setContacts] = useState([]);
  const [modal, setModal] = useState(null); // null | "add" | contact object
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", position: "", isPrimary: false, notes: "" });

  const load = useCallback(async () => {
    try {
      const res = await userRequest.get(`contacts/client/${clientId}`);
      setContacts(res.data.contacts || []);
    } catch { setContacts([]); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm({ firstName: "", lastName: "", email: "", phone: "", position: "", isPrimary: false, notes: "" });
    setModal("add");
  };

  const openEdit = (c) => {
    setForm({ firstName: c.firstName, lastName: c.lastName, email: c.email || "", phone: c.phone || "", position: c.position || "", isPrimary: c.isPrimary, notes: c.notes || "" });
    setModal(c);
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    try {
      if (modal === "add") {
        await userRequest.post("contacts", { ...form, clientId });
        toast.success("Contact ajouté.");
      } else {
        await userRequest.put(`contacts/${modal._id}`, form);
        toast.success("Contact mis à jour.");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce contact ?")) return;
    try {
      await userRequest.delete(`contacts/${id}`);
      toast.success("Contact supprimé.");
      load();
    } catch { toast.error("Erreur suppression."); }
  };

  return (
    <>
      <div className="tab-header">
        <h2>Contacts</h2>
        <button className="add-btn" onClick={openAdd}><UilPlus size={14} /> Ajouter</button>
      </div>

      {contacts.length === 0 ? (
        <div className="empty-tab">Aucun contact enregistré.</div>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Nom</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Téléphone</strong></TableCell>
                <TableCell><strong>Poste</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((c) => (
                <TableRow key={c._id} hover>
                  <TableCell>
                    {c.firstName} {c.lastName}
                    {c.isPrimary && <span className="primary-badge">Principal</span>}
                  </TableCell>
                  <TableCell>{c.email || "—"}</TableCell>
                  <TableCell>{c.phone || "—"}</TableCell>
                  <TableCell>{c.position || "—"}</TableCell>
                  <TableCell>
                    <button className="action-btn" onClick={() => openEdit(c)}><UilEdit size={13} /> Modifier</button>
                    <button className="action-btn danger" onClick={() => handleDelete(c._id)}><UilTrash size={13} /> Supprimer</button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {modal !== null && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>{modal === "add" ? "Ajouter un contact" : "Modifier le contact"}</h3>
            <div className="modal-form">
              {[
                { label: "Prénom *", name: "firstName", placeholder: "Jean" },
                { label: "Nom *", name: "lastName", placeholder: "Dupont" },
                { label: "Email", name: "email", placeholder: "jean@entreprise.com", type: "email" },
                { label: "Téléphone", name: "phone", placeholder: "+33 6 00 00 00 00", type: "tel" },
                { label: "Poste", name: "position", placeholder: "Responsable achat" },
              ].map(({ label, name, placeholder, type = "text" }) => (
                <div className="form-group" key={name}>
                  <label>{label}</label>
                  <input type={type} name={name} value={form[name]} onChange={handleChange} placeholder={placeholder} />
                </div>
              ))}
              <div className="form-group">
                <label>
                  <input type="checkbox" name="isPrimary" checked={form.isPrimary} onChange={handleChange} style={{ marginRight: 8 }} />
                  Contact principal
                </label>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setModal(null)}>Annuler</button>
              <button className="add-btn" onClick={handleSave}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Commercial Terms Tab ──────────────────────────────────────────────────────
const CommercialTermsTab = ({ clientId }) => {
  const [terms, setTerms] = useState({
    discountRate: 0, paymentMode: "bank-transfer", paymentDelay: 30,
    creditLimit: 0, taxRule: "HT", currency: "EUR", freeShippingThreshold: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    userRequest.get(`commercial-terms/${clientId}`)
      .then((res) => {
        const t = res.data.terms;
        setTerms({
          discountRate: t.discountRate ?? 0,
          paymentMode: t.paymentMode ?? "bank-transfer",
          paymentDelay: t.paymentDelay ?? 30,
          creditLimit: t.creditLimit ?? 0,
          taxRule: t.taxRule ?? "HT",
          currency: t.currency ?? "EUR",
          freeShippingThreshold: t.freeShippingThreshold ?? "",
        });
      })
      .catch(() => {}); // 404 = no terms yet, form starts empty
  }, [clientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTerms((p) => ({ ...p, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await userRequest.put(`commercial-terms/${clientId}`, terms);
      toast.success("Conditions commerciales enregistrées.");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="tab-header">
        <h2>Conditions commerciales</h2>
      </div>
      <div className="terms-form">
        <div className="form-group">
          <label>Remise (%)</label>
          <input type="number" name="discountRate" min={0} max={100} value={terms.discountRate} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Mode de paiement</label>
          <select name="paymentMode" value={terms.paymentMode} onChange={handleChange}>
            {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Délai de paiement (jours)</label>
          <input type="number" name="paymentDelay" min={0} value={terms.paymentDelay} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Limite de crédit (€)</label>
          <input type="number" name="creditLimit" min={0} value={terms.creditLimit} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label>Règle de taxe</label>
          <select name="taxRule" value={terms.taxRule} onChange={handleChange}>
            {TAX_RULES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Devise</label>
          <input type="text" name="currency" value={terms.currency} onChange={handleChange} maxLength={3} />
        </div>
        <div className="form-group">
          <label>Livraison offerte dès (€)</label>
          <input type="number" name="freeShippingThreshold" min={0} value={terms.freeShippingThreshold} onChange={handleChange} placeholder="Laisser vide si non applicable" />
        </div>
      </div>
      <button className="save-btn" onClick={handleSave} disabled={saving}>
        {saving ? "Enregistrement..." : "Enregistrer"}
      </button>
    </>
  );
};

// ── Notes CRM Tab ─────────────────────────────────────────────────────────────
const NotesTab = ({ clientId }) => {
  const [notes, setNotes] = useState([]);
  const [modal, setModal] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [form, setForm] = useState({ content: "", type: "other" });

  const load = useCallback(async () => {
    try {
      const res = await userRequest.get(`client-notes/client/${clientId}`);
      setNotes(res.data.notes || []);
    } catch { setNotes([]); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm({ content: "", type: "other" }); setEditNote(null); setModal(true); };
  const openEdit = (n) => { setForm({ content: n.content, type: n.type }); setEditNote(n); setModal(true); };

  const handleSave = async () => {
    try {
      if (editNote) {
        await userRequest.put(`client-notes/${editNote._id}`, form);
        toast.success("Note mise à jour.");
      } else {
        await userRequest.post(`client-notes/client/${clientId}`, form);
        toast.success("Note ajoutée.");
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur.");
    }
  };

  const handlePin = async (id) => {
    try {
      await userRequest.patch(`client-notes/${id}/pin`);
      load();
    } catch { toast.error("Erreur."); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      await userRequest.delete(`client-notes/${id}`);
      toast.success("Note supprimée.");
      load();
    } catch { toast.error("Erreur."); }
  };

  return (
    <>
      <div className="tab-header">
        <h2>Notes CRM</h2>
        <button className="add-btn" onClick={openAdd}><UilPlus size={14} /> Ajouter</button>
      </div>

      {notes.length === 0 ? (
        <div className="empty-tab">Aucune note enregistrée.</div>
      ) : (
        <div className="notes-list">
          {notes.map((n) => (
            <div key={n._id} className={`note-card ${n.pinnedAt ? "pinned" : ""}`}>
              <div className="note-header">
                <div className="note-meta">
                  <span className="note-type-badge">{n.type}</span>
                  <span>
                    {n.authorId ? `${n.authorId.name} ${n.authorId.surname}` : "—"}
                  </span>
                  <span>{new Date(n.createdAt).toLocaleDateString("fr-FR")}</span>
                  {n.pinnedAt && <span style={{ color: "#d97706" }}>📌 Épinglée</span>}
                </div>
                <div>
                  <button className="action-btn" onClick={() => handlePin(n._id)}>
                    {n.pinnedAt ? "Désépingler" : "Épingler"}
                  </button>
                  <button className="action-btn" onClick={() => openEdit(n)}><UilEdit size={13} /></button>
                  <button className="action-btn danger" onClick={() => handleDelete(n._id)}><UilTrash size={13} /></button>
                </div>
              </div>
              <p className="note-content">{n.content}</p>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>{editNote ? "Modifier la note" : "Ajouter une note"}</h3>
            <div className="modal-form">
              <div className="form-group">
                <label>Type</label>
                <select name="type" value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}>
                  {NOTE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Contenu *</label>
                <textarea
                  name="content"
                  value={form.content}
                  onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                  rows={5}
                  placeholder="Détails de l'échange..."
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setModal(false)}>Annuler</button>
              <button className="add-btn" onClick={handleSave}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const formatAddress = (addr) => {
  if (!addr) return "—";
  const parts = [
    addr.street,
    addr.postalCode && addr.city ? `${addr.postalCode} ${addr.city}` : (addr.city || addr.postalCode),
    addr.country,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "—";
};

// ── Main ClientDetail Page ────────────────────────────────────────────────────
const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    userRequest.get(`clients/${id}`)
      .then((res) => setClient(res.data.client))
      .catch(() => toast.error("Client introuvable."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="client-detail"><p>Chargement...</p></div>;
  if (!client) return <div className="client-detail"><p>Client introuvable.</p></div>;

  return (
    <div className="client-detail">
      <div className="client-detail-header">
        <button className="back-btn" onClick={() => navigate("/clients")}>
          <UilArrowLeft size={14} /> Retour
        </button>
        <h1>{client.companyName}</h1>
        {client.status && <span className={`status-badge status-${client.status}`}>{client.status}</span>}
      </div>

      <div className="client-info-card">
        <div className="info-item"><label>Email</label><span>{client.email}</span></div>
        <div className="info-item"><label>Téléphone</label><span>{client.number || "—"}</span></div>
        <div className="info-item"><label>SIRET</label><span>{client.siret || "—"}</span></div>
        <div className="info-item"><label>TVA</label><span>{client.vatNumber || "—"}</span></div>
        <div className="info-item">
          <label>Commercial</label>
          <span>
            {client.assignedWorkerId
              ? `${client.assignedWorkerId.name} ${client.assignedWorkerId.surname}`
              : "—"}
          </span>
        </div>
        <div className="info-item">
          <label>Source</label><span>{client.source || "—"}</span>
        </div>
        <div className="info-item">
          <label>Tags</label>
          <span>{client.tags?.length > 0 ? client.tags.join(", ") : "—"}</span>
        </div>
        <div className="info-item">
          <label><UilMapPin size={13} style={{ marginRight: 4 }} />Adresse de livraison</label>
          <span>{formatAddress(client.deliveryAddress)}</span>
        </div>
        <div className="info-item">
          <label><UilMapPin size={13} style={{ marginRight: 4 }} />Adresse de facturation</label>
          <span>
            {client.billingSameAsDelivery
              ? "Identique à la livraison"
              : formatAddress(client.billingAddress)}
          </span>
        </div>
        {client.internalNotes && (
          <div className="info-item" style={{ gridColumn: "1 / -1" }}>
            <label>Notes internes</label>
            <span>{client.internalNotes}</span>
          </div>
        )}
      </div>

      <div className="tabs-bar">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === i ? "active" : ""}`}
            onClick={() => setActiveTab(i)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 0 && <ContactsTab clientId={id} />}
        {activeTab === 1 && <CommercialTermsTab clientId={id} />}
        {activeTab === 2 && <NotesTab clientId={id} />}
      </div>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default ClientDetail;
